import {
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryObserverOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";

export const defaultQueryOptions: Pick<
  QueryObserverOptions,
  "staleTime" | "refetchOnMount" | "refetchOnWindowFocus" | "retry" | "retryDelay"
> = {
  /** Always treat data as stale so navigating back to a page refetches. */
  staleTime: 0,
  refetchOnMount: "always",
  refetchOnWindowFocus: true,
  retry: 2,
  retryDelay: 1000,
};

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      userErrorMessage?: string;
      userSuccessMessage?: string;
    };
    mutationMeta: {
      userErrorMessage?: string;
      userSuccessMessage?: string;
    };
  }
}

let browserQueryClient: QueryClient | null = null;

export function createQueryClient() {
  const queryCache = new QueryCache({
    onError: (_error, query) => {
      const { meta: { userErrorMessage } = {} } = query;
      if (userErrorMessage) {
        toast.error(userErrorMessage);
      }
    },
    onSuccess: (_data, query) => {
      const { meta: { userSuccessMessage } = {} } = query;
      if (userSuccessMessage) {
        toast.success(userSuccessMessage);
      }
    },
  });

  const mutationCache = new MutationCache({
    onError(_error, _variables, _context, mutation) {
      const { meta: { userErrorMessage } = {} } = mutation;
      if (userErrorMessage) {
        toast.error(userErrorMessage);
      }
    },
    onSuccess(_data, _variables, _context, mutation) {
      const { meta: { userSuccessMessage } = {} } = mutation;
      if (userSuccessMessage) {
        toast.success(userSuccessMessage);
      }
    },
  });

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: defaultQueryOptions,
    },
  });
}

/** Shared client so stream handlers can update the sidebar list cache. */
export function getAppQueryClient() {
  if (typeof window === "undefined") return createQueryClient();
  browserQueryClient ??= createQueryClient();
  return browserQueryClient;
}
